import { BaseDragger } from "./BaseDragger";
import { trackShape } from "@/analytics/drawing-analytics";
import { DraggerType, type Point2D, type Point3D } from "./types";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { AddEntitiesCommand } from "../commands/AddEntitiesCommand";
import { simplifyPolyline } from "../algorithms/smooth";
import { PREVIEW_LINE_STYLE, PREVIEW_WILL_CLOSE_STYLE } from "./previewStyles";
import { noteGraphicsModified } from "../telemetry/updateTelemetry";

/** Minimum distance in pixels between collected points */
const MIN_POINT_DISTANCE = 2;

/**
 * Dragger for freehand/pen drawing.
 * Collects points during continuous drag and creates OdDbPolyline on end.
 */
export class FreehandDragger extends BaseDragger {
  readonly type = DraggerType.Freehand;

  private lastScreenPoint: Point2D | null = null;
  private previewId: string | null = null;
  private screenPoints: Point2D[] = [];
  private closeIndicatorId: string | null = null;

  // ============== Dragger Callbacks ==============

  protected onStart(screen: Point2D, world: Point3D): void {
    // Initialize points array with first point
    this.points = [world];
    this.screenPoints = [screen];
    this.lastScreenPoint = screen;

    // Create preview overlay
    const overlay = this.viewer.overlayManager;
    if (overlay) {
      this.previewId = overlay.createPolyline([screen], PREVIEW_LINE_STYLE);
    }
  }

  protected onDrag(screen: Point2D, world: Point3D): void {
    // Filter points by minimum distance to reduce noise
    if (this.lastScreenPoint) {
      const dx = screen.x - this.lastScreenPoint.x;
      const dy = screen.y - this.lastScreenPoint.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < MIN_POINT_DISTANCE) {
        return;
      }
    }

    // Add point to collection
    this.points.push(world);
    this.screenPoints.push(screen);
    this.lastScreenPoint = screen;

    // Update preview overlay
    const overlay = this.viewer.overlayManager;
    if (overlay && this.previewId) {
      overlay.addPointToPolyline(this.previewId, screen);
    }

    // Update auto-close indicator
    this.updateCloseIndicator(screen);
  }

  protected onEnd(): void {
    // Clear preview first
    this.clearPreview();

    // Need at least 2 points for a polyline
    if (this.points.length < 2) {
      this.clear();
      return;
    }

    // Check if we should auto-close the polyline
    const shouldClose = this.shouldAutoClose();

    // Apply smoothening if enabled
    if (this.viewer.smoothen > 0 && this.points.length > 2) {
      const points3D = this.points.map(
        (p) => [p.x, p.y, p.z] as [number, number, number],
      );
      const simplified = simplifyPolyline(
        points3D,
        this.viewer.smoothen,
        shouldClose, // closeContour - true if auto-closing
        (d) => this.viewer.screenDistanceToWorld(d),
      );
      this.points = simplified.map(([x, y, z]) => ({ x, y, z }));
    }

    // Create the polyline entity
    const entityInfo = this.createPolyline(shouldClose);

    if (entityInfo) {
      const noteConfig = this.viewer.activeNoteConfig;

      // Apply NoteConfig styling to the entity
      this.applyNoteConfigToEntity(entityInfo.id);

      // Collect all entity IDs (main entity + optional hatch)
      const entityIds: DrawingModule.OdDbObjectId[] = [entityInfo.id];

      // Create hatch if the shape is closed and hatch is configured in NoteConfig
      if (shouldClose) {
        const hatchId = this.createHatchForEntity(entityInfo.id);
        if (hatchId) {
          entityIds.push(hatchId);
        }
      }

      // Add all entities in a single command for proper undo/redo
      const command = new AddEntitiesCommand(this.viewer, {
        entityHandles: entityIds.map((id) => getEntityHandle(id)),
        originalLayerName: noteConfig.props.layername ?? "0",
      });

      // Register with history via facade
      this.viewer.executeCommand(command);
      trackShape("freehand");

      // Auto-select if enabled
      this.selectCreatedEntity(entityInfo.id);
      for (const id of entityIds) {
        deleteObj(id);
      }
    }

    // Update display
    this.viewer.update("FreehandDragger.onEnd");

    // Clear state for next stroke
    this.clear();
  }

  protected onCancel(): void {
    // Clear preview and state without creating entity
    this.clearPreview();
    this.clear();
    this.viewer.update("FreehandDragger.onCancel");
  }

  private clearPreview(): void {
    const overlay = this.viewer.overlayManager;
    if (overlay) {
      if (this.previewId) {
        overlay.remove(this.previewId);
        this.previewId = null;
      }
      if (this.closeIndicatorId) {
        overlay.remove(this.closeIndicatorId);
        this.closeIndicatorId = null;
      }
    }
  }

  // ============== Auto-Close Detection ==============

  /**
   * Check if the polyline should be auto-closed.
   * Returns true if auto-close is enabled and the end point is close enough to the start point.
   */
  private shouldAutoClose(): boolean {
    // Check if auto-close is enabled
    if (!this.viewer.autoClose) {
      return false;
    }

    // Need at least 3 points to make a meaningful closed shape
    if (this.screenPoints.length < 3) {
      return false;
    }

    // Get start and end screen points
    const startScreen = this.screenPoints[0];
    const endScreen = this.screenPoints[this.screenPoints.length - 1];

    // Calculate distance in screen pixels
    const dx = endScreen.x - startScreen.x;
    const dy = endScreen.y - startScreen.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Compare against threshold
    return distance <= this.viewer.autoCloseThreshold;
  }

  /**
   * Check if current position is within auto-close threshold.
   */
  private isWithinCloseThreshold(screen: Point2D): boolean {
    if (!this.viewer.autoClose || this.screenPoints.length < 3) {
      return false;
    }

    const startScreen = this.screenPoints[0];
    const dx = screen.x - startScreen.x;
    const dy = screen.y - startScreen.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    return distance <= this.viewer.autoCloseThreshold;
  }

  /**
   * Update the auto-close indicator based on current position.
   * Shows a highlighted circle around the start point when close enough to auto-close.
   */
  private updateCloseIndicator(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    const willClose = this.isWithinCloseThreshold(screen);

    if (willClose) {
      // Show or update the close indicator
      const startScreen = this.screenPoints[0];
      const indicatorRadius = this.viewer.autoCloseThreshold;

      if (this.closeIndicatorId) {
        // Update existing indicator position
        overlay.update(this.closeIndicatorId, {
          cx: startScreen.x,
          cy: startScreen.y,
        });
      } else {
        // Create new indicator
        this.closeIndicatorId = overlay.createCircle(
          startScreen.x,
          startScreen.y,
          indicatorRadius,
          PREVIEW_WILL_CLOSE_STYLE,
        );
      }
    } else {
      // Hide the close indicator when not close enough
      if (this.closeIndicatorId) {
        overlay.remove(this.closeIndicatorId);
        this.closeIndicatorId = null;
      }
    }
  }

  // ============== Polyline Creation ==============

  /**
   * Create OdDbPolyline from collected points.
   * @param closed Whether to close the polyline (auto-close detected)
   * @returns Entity info with object ID, or null on failure
   */
  private createPolyline(
    closed: boolean = false,
  ): { id: DrawingModule.OdDbObjectId } | null {
    const module = this.module;
    const db = this.viewer.database;

    if (!db || this.points.length < 2) {
      return null;
    }

    // Create polyline object
    const polyline = module.OdDbPolyline.createObject();
    polyline.setDatabaseDefaults(db, true);

    // Add all vertices
    for (let i = 0; i < this.points.length; i++) {
      const point = this.points[i];
      const point2d = new module.OdGePoint2d(point.x, point.y);

      // addVertexAt(index, point2d, bulge, startWidth, endWidth, vertexId)
      // bulge = 0 for straight segments
      polyline.addVertexAt(i, point2d, 0, 0, 0, 0);

      deleteObj(point2d);
    }

    // Set closed if auto-close detected
    if (closed) {
      polyline.setClosed(true);
    }

    // Append to current space
    const currentSpaceId = getCurrentSpaceId(db);
    const currentSpace = openAs(
      currentSpaceId,
      module.OdDbBlockTableRecord,
      OpenMode.kForWrite,
    );
    const entityId = currentSpace.appendOdDbEntity(polyline);
    polyline.recordGraphicsModified(true);
    noteGraphicsModified("Polyline");

    deleteObj(currentSpace);
    deleteObj(polyline);

    return { id: entityId };
  }

  // ============== Cleanup ==============

  protected clear(): void {
    super.clear();
    this.lastScreenPoint = null;
    this.screenPoints = [];
  }
}
